import dayjs from 'dayjs';

/**
 * 验证手机号的格式
 * @param {string} phone 手机号码
 * @returns {boolean}
 */
export const isMobile = (phone: string): boolean => /^1[3-9]\d{9}$/.test(phone);

/**
 * 依据 pathname 和 query 创建转义后的 url 函数
 * @param {string} pathname 路径
 * @param {object} query 是一个键值对的参数对象
 */
export const createURLSearch = <T extends Record<string, any>>(pathname: string, query?: T): string => {
    const pathnames = pathname.split('?');
    let search = pathnames.length > 1 && pathnames[pathnames.length - 1] ? pathnames.pop() : '';
    if (query && typeof query === 'object') {
        search = Object.keys(query).reduce((result, key) => {
            if (query[key] || query[key] === 0) {
                const kv = `${key}=${encodeURIComponent(query[key])}`;
                return result ? `${result}&${kv}` : kv;
            }
            return result;
        }, search as string);
    }
    return pathnames.shift() + (search ? `?${search}` : '');
};

/**
 * 验证邮箱格式, 邮箱名可以是字母、数字、点、下划线、中文名
 * @param {string} email 邮箱
 */
// eslint-disable-next-line arrow-body-style
export const isEmail = (email: string): boolean => {
    return /^([A-Za-z0-9_\-.\u4e00-\u9fa5])+@([A-Za-z0-9_-])+\.([A-Za-z]{2,8})$/gi.test(email);
};

/**
 * 验证中文姓名
 * @param {string} name 验证的中文姓名
 * @returns {boolean}
 */
export const isChineseName = (name: string): boolean => /^[\u4e00-\u9fa5]{2,4}$/.test(name);

/**
 * 验证数值, 包括正负数值, 包括小数点在内
 * @param {string | number} num 数值
 * @returns {boolean}
 */
export const isNumber = (value: string | number): boolean => /^[+-]?(\d|[1-9]\d+)(\.\d+)?$/.test(String(value));

// /**
//  * 验证座机号码, 格式: 区号-座机号-分机号, 其中区号和分机号是非必要的
//  * @param {string} phone 座机号码
//  * @returns {boolean}
//  */
// export const isTelephone = (phone: string): boolean => /^([0-9]{3,4}-)?[0-9]{7,8}(-\d{1,5})?$/.test(phone);

/**
 * 验证 url 是否是图片连接, 只验证 png、jpg、jpeg、gif、bmp、webp、svg 这几种类型
 * @param {string} url 验证的图片连接
 * @returns {boolean}
 */
export const isImgURL = (url: string): boolean => /\.(png|jpe?g|gif|bmp|webp|svg)(\?.*)?$/.test(url);

/**
 * 验证 URL 地址, 简写版, 只判断 https
 * @param {string} url 地址
 * @returns {boolean}
 */
export const isURL = (url: string): boolean => /^https?:\/\/.*/.test(url);

/**
 * 验证网址, 简写版, 不需要检测 https 或 http
 * @param {string} url 地址
 * @returns {boolean}
 */
export const isWebsite = (url: string): boolean => /[\w-]+\.[^.]+$/.test(url);

/**
 * 解析 json 方法
 * @param {string} json json 数据
 * @param {T} defaultValue 解析错误返回的默认值
 * @returns T
 */
export const toJson = <T = unknown>(json: string, defaultValue?: T): T => {
    try {
        return JSON.parse(json);
    } catch (e) {
        return defaultValue as T;
    }
};

/**
 * 数据类型验证函数
 * @param {string} type 类型的名称
 * @returns {Function}
 */
const validateType =
    <T>(type: string) =>
        (value: unknown): value is T => {
            return Object.prototype.toString.call(value) === `[object ${type}]`;
        };

// 声明 validate 的索引值类型
export type ValidateKey =
    | 'isNull'
    | 'isArray'
    | 'isObject'
    | 'isNumber'
    | 'isString'
    | 'isSymbol'
    | 'isBoolean'
    | 'isPromise'
    | 'isFunction'
    | 'isUndefined';
/**
 * 数据类型验证对象
 * @property {(value: unknown): value is number} isNumber 判断是否是数值类型
 * @property {(value: unknown): value is string} isString 判断是否是字符串类型
 * @property {(value: unknown): value is boolean} isBoolean 判断是否是布尔值类型
 * @property {(value: unknown): value is null} isNull 判断是否是 null 类型
 * @property {(value: unknown): value is undefined} isUndefined 判断是否是 undefined 类型
 * @property {(value: unknown): value is symbol} isSymbol 判断是否是 symbol 类型
 * @property {(value: unknown): value is (...arg: any[]) => any} isFunction 判断是否是函数类型
 * @property {(value: unknown): value is object} isObject 判断是否是对象类型
 * @property {(value: unknown): value is any[]} isArray 判断是否是数组类型
 * @property {(value: unknown): value is Promise<any>} isPromise 判断是否是 Promise 类型
 */
export const validate = {
    isNumber: validateType<number>('Number'),
    isString: validateType<string>('String'),
    isBoolean: validateType<boolean>('Boolean'),
    isNull: validateType<null>('Null'),
    isUndefined: validateType<undefined>('Undefined'),
    isSymbol: validateType<symbol>('Symbol'),
    isFunction: validateType<(...args: any[]) => any>('Function'),
    isObject: validateType<Record<string | number, any>>('Object'),
    isArray: validateType<any[]>('Array'),
    isPromise: validateType<Promise<any>>('Promise')
};

// 声明 os 的索引值
type SysKey =
    | 'isQQ'
    | 'isIOS'
    | 'isIpad'
    | 'isIphone'
    | 'isMobile'
    | 'isWebApp'
    | 'isWebKit'
    | 'isWx'
    | 'isWxWork'
    | 'isAndroid';
/**
 * 系统 ua 检测对象
 * @property {boolean} isQQ 判断是否是 qq
 * @property {boolean} isIOS 判断是否是 ios
 * @property {boolean} isIPad 判断是否是 iPad
 * @property {boolean} isWebKit 判断是否是 WebKit 内核
 * @property {boolean} isMobile 判断是否是移动终端
 * @property {boolean} isIPhone 判断是否是 iPhone 或者 QQHD 浏览器
 * @property {boolean} isWebApp 判断是否是 web 应该程序, 没有头部与底部
 * @property {boolean} isWx 判断是否是微信
 * @property {boolean} isWxWork 判断是否是企业微信
 * @property {boolean} isAndroid 判断是否是 android
 */
export const sys = ((): Record<SysKey, boolean> => {
    // 获取 userAgent 信息
    // 小程序里边会报错：Cannot read property 'userAgent' of undefined
    const ua: string = navigator.userAgent;
    // 返回结果
    return {
        isQQ: !!ua.match(/\sQQ/i),
        isIOS: !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/),
        isIpad: ua.indexOf('iPad') > -1,
        isWebKit: ua.indexOf('AppleWebKit') > -1,
        isMobile: !!ua.match(/AppleWebKit.*Mobile.*/),
        isIphone: ua.indexOf('iPhone') > -1,
        isWebApp: ua.indexOf('Safari') === -1,
        isWxWork: ua.indexOf('wxwork') > -1 && ua.indexOf('MicroMessenger') > -1,
        isWx: ua.indexOf('wxwork') === -1 && ua.indexOf('MicroMessenger') > -1,
        isAndroid: ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1
    };
})();

/**
 * 延迟函数
 * @param {number} timeout 毫秒数, 默认值为 0
 * @returns {promise}
 */
export const delay = (timeout = 0): Promise<unknown> => {
    return new Promise<unknown>((resolve) => window.setTimeout(resolve, timeout));
};

// 声明 toAwait 的返回类型
export type ToAwaitType<T> = [Error | undefined, T | undefined];
/**
 * 简化 async/await 使用 try/catch 的操作函数
 * @param {Promise} promise promise对象
 * @returns {promise}
 */
export const toAwait = async <R>(promise?: Promise<R>): Promise<ToAwaitType<R>> => {
    if (promise && validate.isPromise(promise)) {
        return promise.then<ToAwaitType<R>>((d) => [undefined, d]).catch((e) => [e, undefined]);
    }
    return [new Error('promise is Error'), undefined];
};

/**
 * 补零参数
 * @param {string|number} num 必选, 需要补零的参数
 * @param {number} len 可选, 补零参数的长度为多小, 不够才会补零, 默认为2
 * @returns {string}
 */
export const fillNil = (num: string | number, len = 2): string => {
    // 转字符串
    let str: string = num.toString();
    // 根据长度计算补 0
    while (str.length < len) {
        str = `0${str}`;
    }
    // 返回结果
    return str;
};

/**
 * 简单随机数生成函数, 使用了闭包函数, 定义一个 count, 用于累加, 方便用于排序
 * @returns {string}
 */
export const generateRandom = ((): (() => string) => {
    let count = 0;
    return (): string => {
        count += 1000;
        return String(count + Date.now());
    };
})();

/**
 * 时间戳转化时间的函数
 * @param {number} timeStamp 必选, 时间戳
 * @param {string} separtor 分隔符, 默认是 -
 * @returns {string}
 */
export const formatTime = (timeStamp: number, separtor = '-'): string => {
    // 时间戳转成日期
    const date: Date = new Date(timeStamp);
    // 返回结果
    return `${date.getFullYear()}${separtor}${fillNil(date.getMonth() + 1)}${separtor}${fillNil(date.getDate())} 
            ${fillNil(date.getHours())}:${fillNil(date.getMinutes())}:${fillNil(date.getSeconds())}`;
};

/**
 * 日期格式化函数
 * @param {string} format 日期的格式
 * @param {Date} date 日期对象
 * @returns {string}
 */
export const formatDate = (date: Date = new Date(), format = 'YYYY-MM-DD'): string => {
    // format 的规范值, 只能用以下的字母表示
    const specs: string[] = 'YYYY:MM:DD:HH:mm:ss'.split(':');
    // toISOString 函数是根据世界时间返回日期字符串, 因此需要纠正时区之差
    const localISODate: Date = new Date(date.getTime() - new Date().getTimezoneOffset() * 60 * 1000);
    // 返回指定格式的日期(format)
    return localISODate
        .toISOString()
        .split(/[-:.TZ]/)
        .reduce((prev, item, i) => prev.split(specs[i]).join(item), format);
};

/**
 * 转化 url 的 query 参数为对象的函数
 * @param {string} url 需要转化的 query 的 url, 默认值是当前页面的 url
 * @returns {object}
 */
export const parseQuery = <T extends Record<any, any> = any>(url: string = window.location.search): T => {
    const params = <T>{};
    const querys: string = url.slice(url.indexOf('?') + 1);
    querys.replace(/([^?=/&]+)(=?)([^?=/&]*)/g, (s: string, k: string, _d: any, v: any = ''): string => {
        params[k as keyof T] = decodeURIComponent(v) as T[keyof T];
        return s;
    });
    return params;
};

/**
 * 对表格传参数据进行格式化处理
 * @returns Record<string, Dayjs[]>
 */
export const formatTableParams = (data: Record<string, any>) => {
    return Object.entries(data).map(([k, v]: any) => {
        if (Array.isArray(v)) {
            return {
                name: k,
                opt: 'in',
                val: JSON.stringify(v)
            };
        }
        // 处理数据
        if (typeof v === 'object' && Object.keys(v || {}).indexOf('value') > -1) {
            return {
                name: k,
                val: v.value ?? ''
            };
        }
        if (typeof v === 'object' && v?.opt) {
            return {
                name: k,
                opt: v.opt,
                val: v.val
            };
        }
        return {
            name: k,
            opt: 'like',
            val: v ?? ''
        };
    });
};

/**
 * 金额格式化函数, 如果以现代浏览器为主, 可以直接使用内置的 toLocaleString
 * @param {string|number} value 格式化的金额
 * @param {number} digits 保留的位数
 * @param {string} delimiter 分隔符
 * @returns {string}
 */
export const formatCurrency = (value: string | number, digits = 2, delimiter = ',') => {
    const currentValue = `${value ?? ''}`;
    if (isNumber(currentValue)) {
        const [intValue, decimalValue = ''] = currentValue.split('.');
        const formatInt = intValue.replace(/(\d)(?=(\d{3})+$)/g, `$1${delimiter}`);
        return `${formatInt}.${(decimalValue + new Array(digits).fill(0).join('')).slice(0, digits)}`;
    }
    return currentValue;
};

export const convertCurrencyToNumber = (currency: string | number): number | string => {
    const _currency = (currency || '').toString();
    // 检查是否符合金额格式
    const isValidFormat = /^\d{1,3}(,\d{3})*\.\d{2}$/.test(_currency);
    if (!isValidFormat) {
        // 如果不符合
        return _currency;
    }
    // 移除逗号
    const numericString = _currency.replace(/,/g, '');
    // 转换为数字
    return parseFloat(numericString);
};

export const formatNumberToTenThousand = (num: number): string => {
    const units = ['', '万', '亿', '万亿']; // 单位数组
    let minus = false;
    if (num < 0) {
        minus = true;
    }
    let unitIndex = 0; // 单位索引
    let formattedNum = Math.abs(num); // 格式化后的数字
    let unit = ''; // 当前单位

    while (formattedNum >= 10000) {
        formattedNum /= 10000;
        unitIndex += 1;
    }

    // 使用 toFixed 函数保留两位小数，并将数字转为字符串
    const _formattedNum = formattedNum.toFixed(2);
    unit = units[unitIndex];

    return `${minus ? '-' : ''}${_formattedNum}${unit}`;
};

/**
 * 简单修复精度丢失的方法
 * @param {string | number} num 转换的数值
 * @param {number} digits 精度保留的位数
 * @param {'string' | 'number'} type 返回的类型
 * @returns {number}
 */
export const fixPrecision = (
    value?: string | number,
    digits = 2,
    type: 'string' | 'number' = 'string'
): string | number => {
    // const currentValue = `${value || 0}`;
    // if (isNumber(currentValue)) {
    //     const radix = Number(`1e+${digits + 1}`);
    //     const result = Number(currentValue) * radix;
    //     const [integerValue, decimalValue = ''] = `${(Math[result < 0 ? 'ceil' : 'floor'](result) / radix)}`.split('.');
    //     const returnValue = `${integerValue}.${(decimalValue + new Array(digits).fill(0).join('')).slice(0, digits)}`;
    //     return type === 'number' ? Number(returnValue) : returnValue;
    // }
    // return Number(currentValue);
    const currentValue = Number(value || 0).toFixed(digits);
    return type === 'number' ? Number(currentValue) : currentValue;
};

/**
 * 转化数值为百分比的函数, 注意, 由于 JS 的小数是双精度的, 转换时可能会出现很多小数位, 因此需要使用 toFixed(10) 转化
 * @param {number} n 转化的数值
 * @returns {string}
 */
export const percent = (n?: number, digits?: number): string | undefined =>
    n === null || n === undefined ? n : `${fixPrecision(parseFloat((n * 100).toFixed(14)), digits || 2)}%`;

/**
 * 将数值金额转换为中文大写形式的函数
 * @param {string | number} value - 待转换的数值，可以是数字或其字符串表示形式
 * @returns {string | undefined} - 如果输入有效，返回转换后的中文大写金额字符串；如果输入无效，返回 undefined
 */
export const digitToCn = (value: string | number): string => {
    // 对 0 值进行特殊处理，直接返回对应的中文大写形式
    if (value === 0 || !value || value === '0.00') {
        return '零元整';
    }

    // 确保输入值为数字类型，如果是字符串则尝试将其转换为数字
    const numberValue = typeof value === 'number' ? value : Number(value);

    // 判断数值是否为负数，并据此设置标记
    const isNegative = numberValue < 0;

    // 定义数字到中文的映射和单位
    const digitCn = '零壹贰叁肆伍陆柒捌玖';
    let digitUnit = '仟佰拾兆仟佰拾亿仟佰拾万仟佰拾元角分';

    // 将数值转换为字符串，并在末尾补充两个零以便于处理小数部分
    let strValue = `${Math.abs(numberValue)}00`;

    // 如果原始数值包含小数点，进行相应的处理
    const indexPoint = strValue.indexOf('.');
    if (indexPoint >= 0) {
        strValue = strValue.substring(0, indexPoint) + strValue.slice(indexPoint + 1, indexPoint + 3);
    }

    // 调整单位字符串的长度以匹配数值字符串的长度
    digitUnit = digitUnit.substring(digitUnit.length - strValue.length);

    // 初始化输出字符串
    let output = '';

    // 遍历数值字符串，将每个数字转换为对应的中文大写，并附加单位
    for (let i = 0; i < strValue.length; i++) {
        output += digitCn.charAt(Number(strValue.charAt(i))) + digitUnit.charAt(i);
    }

    // 对输出字符串进行优化，合并连续的零，处理特殊单位
    output = output
        .replace(/零(仟|佰|拾|角)/g, '零')
        .replace(/(零)+/g, '零')
        .replace(/零(兆|亿|万|元)/g, '$1')
        .replace(/亿万/g, '亿')
        .replace(/兆亿/g, '兆')
        .replace(/^元零?|零分/g, '')
        .replace(/元$/g, '元整');

    // 如果原始数值为负数，在结果前加上“负”字
    return isNegative ? `负${output}` : output;
};

// 验证文件类型
export function getFileType(fileName: string): string {
    const extension = fileName?.split('.').pop()?.toLowerCase();

    if (extension === 'doc' || extension === 'docx') {
        return 'word';
    } else if (extension === 'xls' || extension === 'xlsx') {
        return 'excel';
    } else if (extension === 'pdf') {
        return 'pdf';
    } else if (extension && ['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
        return 'image';
    } else {
        return 'file'; // 未知文件类型
    }
}

/**
 * 常见的恶意文件名检验
 * @param {string} fileName 文件名
 * @returns {boolean}
 */
export const isMaliciousFileName = (fileName: string): boolean => {
    // 常见的恶意文件名模式
    const maliciousPatterns = [
        /exe$/,
        /bat$/,
        /sh$/,
        /js$/,
        /vbs$/,
        /ini$/,
        /cfg$/,
        /rar$/,
        /php$/,
        /asp$/,
        /jsp$/,
        /dll$/,
        /bin$/
        // 添加其他模式...
    ];

    for (const pattern of maliciousPatterns) {
        if (pattern.test(fileName.toLowerCase())) {
            return true;
        }
    }

    return false;
};

/**
 * 文件名检验
 * @param {string} fileName 文件名
 * @returns {boolean}
 */
export const testFileName = (fileName: string, patterns: string[]): boolean => {
    // 常见的恶意文件名模式
    const maliciousPatterns = patterns.map((item: string) => {
        return eval(`/${item}$/`);
    });

    for (const pattern of maliciousPatterns) {
        if (pattern.test(fileName.toLowerCase())) {
            return true;
        }
    }

    return false;
};

/**
 * 截取姓名后两位（姓名-内容）
 * @param {string} fileName 文件名
 * @returns {boolean}
 */
export const interceptionName = (text: string): string => {
    const name = text.split('-')[0];
    return name && name.length > 2 ? name.slice(name.length - 2, name.length) : name;
};

/**
 * 判断是否为空，为空不显示的工具函数
 * 为0：显示，
 * 为null、undefined、[]、''，不显示
 */
export const isEmpty = (value: any) => {
    if (value === 0) {
        return true;
    }
    if (value === null || value === undefined || (Array.isArray(value) && value.length <= 0) || value === '') {
        return false;
    }
    return true;
};

// 富文本判断
export const isRichText = (input: string): boolean => {
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;
    return htmlRegex.test(input);
};

/**
 * 富文本转文本
 * @param {string} html 富文本
 * @returns {string}
 */
export const getSimpleText = (html: string) => {
    const re1 = new RegExp('<.+?>', 'g'); //匹配html标签的正则表达式，"g"是搜索匹配多个符合的内容
    const msg = html.replace(re1, ''); //执行替换成空字符
    return msg;
};

// uniapp 判断设备端（PC/移动）
export const isPC = (): boolean => {
    const { deviceType = 'unknow' } = uni.getSystemInfoSync();
    return deviceType === 'pc';
};

export function base64ToBlob(base64: string) {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n > 0) {
        n -= 1;
        u8arr[n] = bstr.charCodeAt(n);
    }
    const theBlob = new Blob([u8arr], {
        type: mime
    });
    const fileName = `${new Date().getTime()}.png`;
    return new File([theBlob], fileName);
}

export const arabicToChinese = (num: number) => {
    const chineseNums = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
    const units = ['', '十', '百', '千', '万', '十', '百', '千', '亿'];

    if (num === 0) return '零';

    let result = '';
    let unitIndex = 0;

    while (num > 0) {
        const digit = num % 10;
        if (digit !== 0 || result === '' || units[unitIndex] === '万' || units[unitIndex] === '亿') {
            result = chineseNums[digit] + units[unitIndex] + result;
        } else if (result[0] !== '零') {
            result = `零${result}`;
        }
        num = Math.floor(num / 10);
        unitIndex += 1;
    }

    // 去除多余的“零”
    result = result.replace(/零+/g, '零');
    result = result.replace(/零([亿|万])/g, '$1');

    return result;
};

export const amountToUnitString = (amount: number): string => {
    if (amount === 0) return '0';

    const integerPart = Math.floor(amount);
    const decimalPart = Math.round((amount - integerPart) * 100);

    let result = '';

    // 处理整数部分
    const strAmount = integerPart.toString().padStart(9, '0'); // 补足到15位
    for (let i = 0; i < strAmount.length; i++) {
        const digit = parseInt(strAmount[i]);
        if (i < 9) {
            // 只处理前11位
            result += digit.toString();
        }
    }

    // 处理小数部分
    if (decimalPart >= 0) {
        const strDecimal = decimalPart.toString().padStart(2, '0');
        for (let i = 0; i < strDecimal.length; i++) {
            const digit = parseInt(strDecimal[i]);
            result += digit.toString();
        }
    }

    const resultStr = result.replace(/^0+/g, (match) => '-'.repeat(match.length));
    console.log(resultStr);

    return resultStr;
};

/**
 * 计算合同开始日期到结束日期的周期天数
 * @param startDateStr 合同开始日期，格式为 YYYY-MM-DD
 * @param endDateStr 合同结束日期，格式为 YYYY-MM-DD
 * @returns 周期天数
 */
export const calculateContractDuration = (startDateStr?: string, endDateStr?: string): number => {
    if (!startDateStr || !endDateStr) return 0;
    const startDate = dayjs(startDateStr);
    const endDate = dayjs(endDateStr);

    if (!startDate.isValid() || !endDate.isValid()) {
        throw new Error('Invalid date format. Please use YYYY-MM-DD.');
    }

    const duration = endDate.diff(startDate, 'day') + 1;
    return duration;
};

/**
 * 从文本中提取结构化联系信息（支持中英文格式）
 *
 * @param text - 需要解析的原始文本，可能包含姓名、地址、电话等信息
 * @returns 包含三个属性的对象：
 *   name - 提取的姓名（中文或英文格式），未找到返回null
 *   address - 匹配到的地址信息，按优先级返回最完整格式，未找到返回null
 *   phone - 符合中国手机号格式（11位/1开头）的电话号码，未找到返回null
 */
export const improvedExtractContactInfo = (
    text: string
): {
    name: string | null;
    address: string | null;
    phone: string | null;
} => {
    // 初始化结果对象
    const result = {
        name: null as string | null,
        address: null as string | null,
        phone: null as string | null
    };

    // 提取中国手机号 (11位数字，通常以1开头)
    const phoneRegex = /(?:1[3-9]\d{9})/g;
    const phoneMatch = text.match(phoneRegex);
    if (phoneMatch) {
        result.phone = phoneMatch[0];
    }

    // 姓名识别（改进版）
    // 中文姓名（2-4个汉字）可能后跟称谓
    const chineseNameRegex = /([\u4e00-\u9fa5]{2,4})(?:先生|小姐|女士|男士|同志)?/;
    // 英文名优化，支持更多格式
    const englishNameRegex = /([A-Za-z]+[-.]?[A-Za-z]*(?:\s[A-Za-z]+[-.]?[A-Za-z]*){0,2})/;

    const chineseNameMatch = text.match(chineseNameRegex);
    const englishNameMatch = text.match(englishNameRegex);

    if (chineseNameMatch) {
        result.name = chineseNameMatch[1]; // 使用捕获组，排除称谓
    } else if (englishNameMatch) {
        result.name = englishNameMatch[1];
    }

    // 地址识别（改进版）
    // 1. 标准格式: XX省XX市XX区/县XX路XX号
    const standardAddressRegex = /(?:[\u4e00-\u9fa5]{2,}[省市区县])+[\u4e00-\u9fa5]*[路街道][^，。,\d]*\d+号?/;
    // 2. 简化格式: XX路XX号，没有省市区
    const simpleAddressRegex = /[\u4e00-\u9fa5]+[路街道巷][\u4e00-\u9fa5]*\d+号?/;
    // 3. 小区/大厦格式
    const communityAddressRegex =
        /[\u4e00-\u9fa5]+(?:小区|大厦|公寓|花园|广场)[\u4e00-\u9fa5\d]*(?:\d+[栋幢座号楼]-?\d+)?/;
    // 4. 门牌号格式
    const doorNumberRegex = /[\u4e00-\u9fa5]+(?:号楼|单元|室)[\d-]+/;

    // 按优先级尝试匹配地址
    const addressMatches = [
        text.match(standardAddressRegex),
        text.match(simpleAddressRegex),
        text.match(communityAddressRegex),
        text.match(doorNumberRegex)
    ];

    for (const match of addressMatches) {
        if (match) {
            result.address = match[0];
            break;
        }
    }

    return result;
};

/**
 * 通过元素 ID 获取其高度 (使用 uni.createSelectorQuery)
 * @param {string} elementId - 元素的 ID
 * @param {object} [componentInstance=null] - 组件实例上下文。
 *                                            在自定义组件内部调用时，需要传入组件实例 (通常是 this) 来限定查询范围。
 *                                            在页面的 <script setup> 中，查询通常已自动限定在当前页面，此参数可省略或传 null。
 *                                            如果此函数是独立的工具函数，调用者需要处理上下文。
 * @returns {Promise<number | null>} 返回一个 Promise，成功时解析为元素高度 (数字类型)，如果未找到元素或发生错误则解析为 null。
 */
export const getElementHeightById = (elementId: string, componentInstance = null) => {
    return new Promise((resolve, reject) => {
        const query = uni.createSelectorQuery();
        // 如果提供了 componentInstance (例如，在选项式 API 的组件方法中传递 `this`，
        // 或者在组合式 API 中获取当前组件实例)，则使用 .in() 限定查询范围。
        // 在页面的 <script setup> 中，query 默认作用于当前页面。
        if (componentInstance) {
            query.in(componentInstance);
        }

        query
            .select(`#${elementId}`)
            .boundingClientRect((data) => {
                console.log(data, 'getElementHeightById');
                if (data && typeof data.height === 'number') {
                    resolve(data.height);
                } else {
                    console.error(
                        `[getElementHeightById] 未找到 ID 为 #${elementId} 的元素，或无法获取其高度。返回数据:`,
                        data
                    );
                    resolve(null); // 未找到或数据格式错误时，Promise 解析为 null
                }
            })
            .exec();
    });
};
